<template>
  <Pano>
    <div v-if="page.abilities" class="character-sheet">
      <div class="character-sheet-content">
        <div class="part information">
          <div v-if="stats.portrait" class="portrait">
            <img :src="stats.portrait" :alt="stats.title" />
          </div>
          <div class="name container vertical">
            <h2>{{ stats.title }}</h2>
            <CharacterSubtitle :item="stats" />
          </div>
        </div>
        <PlayerAttributes :stats="stats" class="part" />
        <PlayerHitpoints :stats="stats" class="part" />
        <PlayerSaves :stats="stats" class="part">
          <div v-if="page.toggles" class="toggles noprint">
            <h4>Toggles</h4>
            <div class="container horizontal">
              <StatisticIconToggle
                v-for="(_toggle, name) in page.toggles"
                :key="name"
                v-model="activeToggles[name]"
                :name="`${name}`"
                condensed
              />
            </div>
          </div>
        </PlayerSaves>
        <PlayerSkills :stats="stats" class="part" />
        <div class="part passives">
          <h4>Senses & Passives</h4>
          <div class="container horizontal">
            <StatisticIcon
              name="Percep"
              icon="game-icons:human-ear"
              :value="passivePerception"
              unit=" "
              constant
              title="Passive Perception"
              condensed
            />
            <StatisticIcon
              name="Invest"
              icon="game-icons:magnifying-glass"
              :value="passiveInvestigation"
              unit=" "
              constant
              title="Passive Investigation"
              condensed
            />
            <StatisticIcon
              name="Insight"
              icon="game-icons:bow-tie-ribbon"
              :value="passiveInsight"
              unit=" "
              constant
              title="Passive Insight"
              condensed
            />
          </div>
          <p>
            <b>Languages:</b>&nbsp;<em>{{ stats.languages }}</em>
          </p>
          <div class="restbuttons container horizontal noprint">
            <button @click="emitRest('sr', 'lr')">Long&nbsp;Rest</button>
            <button @click="emitRest('sr')">Short&nbsp;Rest</button>
          </div>
        </div>
        <div class="part box-text">
          <PlayerSpellcasting v-for="(data, i) in playerSpellcastingData" :key="i" :stats="stats" :data="data" />
          <Spellcasting v-for="(data, i) in spellcastingData" :key="i" :stats="stats" :data="data" player />
          <slot v-bind="mods" />
        </div>
      </div>
    </div>
  </Pano>
</template>

<script setup lang="ts">
/*
 * TODO:
 *  - Better multiclassing support
 *    - hitdice, class text, mods
 *  - support prepared casters
 *  - Concentration toggle
 *  - jump distance?
 */

import _ from 'lodash'
import { type CharacterSheetData, resolveMods, skillExpr, emitRest } from '../statblock/statblock'
import Spellcasting from '../statblock/Spellcasting.vue'
import PlayerSaves from '../charactersheet/PlayerSaves.vue'
import PlayerSpellcasting from '../charactersheet/PlayerSpellcasting.vue'
import PlayerSkills from '../charactersheet/PlayerSkills.vue'
import PlayerHitpoints from '../charactersheet/PlayerHitpoints.vue'
import PlayerAttributes from '../charactersheet/PlayerAttributes.vue'
import StatisticIcon from '../charactersheet/StatisticIcon.vue'
import StatisticIconToggle from '../charactersheet/StatisticIconToggle.vue'
import { arrayify, clamp } from '../util'

const page = useDocument()
const activeToggles = ref<Record<string, boolean>>({})
const stats = computed(() => {
  const res = {}
  const toggles = Object.entries(page.value.toggles || {})
    .filter(([name]) => activeToggles.value[name])
    .map(([, data]) => data)
  _.merge(res, page.value, ...toggles)
  return res as CharacterSheetData
})
provide('toggles', () => activeToggles.value)

const exhaustion = useResource(ref('exhaustion'))
onRest('lr', () => {
  exhaustion.value = clamp(exhaustion.value - 1, 0, 6)
})

const mods = computed(() => resolveMods(stats.value, { exh: -2 * exhaustion.value, exhdc: -exhaustion.value }))
provide('mods', () => mods.value)

const passivePerception = computed(() => skillExpr(stats.value, 'perception', true))
const passiveInvestigation = computed(() => skillExpr(stats.value, 'investigation', true))
const passiveInsight = computed(() => skillExpr(stats.value, 'insight', true))

const playerSpellcastingData = computed(() => arrayify(stats.value.spells ?? []))
const spellcastingData = computed(() => {
  if (playerSpellcastingData.value.length) return null
  return arrayify(stats.value.spellcasting || [])
})
</script>

<style scoped lang="postcss">
.character-sheet {
  width: 1100px;
  max-width: calc(100vw - 1em); /* subtract margins */
  margin: 1em 0.5em 0;
  overflow: scroll;
  border: 2px solid var(--primary);
  border-radius: 1em;

  .character-sheet-content {
    padding: 1em;

    display: grid;
    gap: 0.5em 1em;
    grid-template-areas:
      'info info info'
      'attributes attributes hitpoints'
      'saves text text'
      'skills text text'
      'passives text text';
    grid-template-columns: 200px 1fr 1fr;
    grid-template-rows: min-content min-content min-content min-content 1fr;
  }

  .part {
    h3,
    h4 {
      margin-top: 0;
      text-align: center;
    }

    button {
      margin: 0;
    }
  }

  .restbuttons {
    align-self: flex-start;
    flex-direction: column;
    gap: 0.25em;

    button {
      padding: 0.25em 0.5em;
    }
  }

  .information {
    grid-area: info;

    display: flex;
    flex-direction: row;
    gap: 1em;

    .portrait {
      height: 57px;
      width: 57px;

      img {
        max-width: 100%;
        border-radius: 0.25em;
        border: 2px solid var(--primary);
      }
    }

    .name {
      flex: 1;
    }

    .subtitle {
      margin: 0;
    }

    h2 {
      margin: 0;
    }
  }

  .passives {
    grid-area: passives;

    h4 {
      border-bottom: 2px solid var(--primary);
      margin-bottom: 0.25em;
    }

    p {
      font-size: 0.9em;
      margin-bottom: 0.5em;
      text-align: center;
    }

    .toggles {
      margin-bottom: 0.25em;
    }
  }

  .box-text {
    grid-area: text;
    column-count: 2;
    column-gap: 1em;
  }

  .conditions {
    grid-area: conditions;

    .hprow {
      justify-content: space-between;

      .restbuttons {
        gap: 0.5em;

        button {
          padding: 0 0.5em;
        }
      }
    }
  }

  .stats {
    grid-area: stats;

    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  @media (min-width: 601px) and (max-width: 950px) {
    .character-sheet-content {
      grid-template-areas:
        'info info'
        'attributes attributes'
        'hitpoints hitpoints'
        'saves text'
        'skills text'
        'passives text';
      grid-template-columns: 200px 1fr;
      grid-template-rows: min-content min-content min-content min-content min-content 1fr;
    }

    .conditions {
      padding-bottom: 0.5em;
    }

    .box-text {
      column-count: 1;
    }
  }

  @media (max-width: 600px) {
    .character-sheet-content {
      padding: 0.5em;
      grid-template-areas:
        'info info'
        'attributes attributes'
        'hitpoints hitpoints'
        'saves skills'
        'passives skills'
        'text text';
      grid-template-columns: 1fr 1fr;
      grid-template-rows: min-content min-content min-content min-content min-content 1fr;
    }

    .conditions {
      padding-bottom: 0.5em;
    }

    .box-text {
      column-count: 1;
    }
  }
}
</style>

<style>
.character-sheet {
  .box-text {
    ul {
      padding-left: 0.5em;
    }
  }
}
</style>
